import { getContext, setContext } from 'svelte';
import { log } from '@shared/log.browser.ts';
import { createTileListOnEx } from '@tilelist/application/core/OnExFactory.ts';
import type { TileListInitialSession } from '@tilelist/application/core/TileListInitialSession.ts';
import type { TileListPageLoadResult } from '@tilelist/application/core/TileListSession.ts';
import type { EventsIncoming } from '@tilelist/application/ports/EventsIncoming.ts';
import type { EventsOutgoing } from '@tilelist/application/ports/EventsOutgoing.ts';
import type { SelectionEvents, SelectionUpdate } from '@tilelist/application/ports/SelectionEvents.ts';
import type { TrackingPort } from '@tilelist/application/ports/TrackingPort.ts';
import type { VariationLoader } from '@tilelist/application/ports/VariationLoader.ts';
import { createFullListLifecycle } from '@tilelist/application/wiring/FullListLifecycle.browser.ts';
import { createOrderedPageLoads } from '@tilelist/application/wiring/OrderedPageLoads.browser.ts';
import type { OrderedPageLoads } from '@tilelist/application/wiring/OrderedPageLoads.browser.ts';
import type { TileListBrowserWiring } from '@tilelist/application/wiring/TileListBrowserWiring.browser.ts';
import { ProductTileController } from '@tilelist/controllers/ProductTileController.browser.ts';
import type { TileListPageLayout } from '@tilelist/domain/composition/TileListPage.ts';
import type { OnEx } from '@tilelist/domain/experiments/OnEx.ts';

export type TileListWiring = {
    onex: OnEx;
    trackingService: TrackingPort | null;
    variationLoadingService: VariationLoader | null;
    controller: ProductTileController | null;
    eventsIncoming: EventsIncoming | null;
    eventsOutgoing: EventsOutgoing | null;
    selectionEvents: SelectionEvents | null;
    restoreScrollPosition: () => Promise<void>;
    saveScrollSelector: () => void;
};

export const WIRING_KEY: unique symbol = Symbol('TileListWiring');

const logger = log.scope('wiring');

/**
 *
 *
 *
 *
 *
 */
export function createAndProvideWiring(initialSession: TileListInitialSession): TileListWiring {
    const onex = createTileListOnEx(initialSession.session);
    const wiring = $state<TileListWiring>({
        onex,
        trackingService: null,
        variationLoadingService: null,
        controller: null,
        eventsIncoming: null,
        eventsOutgoing: null,
        selectionEvents: null,
        restoreScrollPosition: () => Promise.resolve(),
        saveScrollSelector: () => {},
    });
    setContext(WIRING_KEY, wiring);
    return wiring;
}

/**
 *
 *
 *
 *
 *
 *
 *
 */
export function mountWiring(
    ctx: TileListWiring,
    browserWiring: TileListBrowserWiring,
    onReload: (result: TileListPageLoadResult) => void | Promise<void>,
): { cleanup: () => void } {
    return mountBrowserWiring(ctx, browserWiring, onReload, null);
}

export function mountOrderedFullListWiring(
    ctx: TileListWiring,
    browserWiring: TileListBrowserWiring,
    initialPage: TileListPageLayout,
    onReload: (result: TileListPageLoadResult) => void | Promise<void>,
    onInitialRestored: (page: TileListPageLayout) => void,
): { cleanup: () => void } {
    const pageLoads = createOrderedPageLoads(browserWiring.session);
    const lifecycle = createFullListLifecycle(browserWiring, pageLoads);
    const mounted = mountBrowserWiring(ctx, browserWiring, onReload, pageLoads, (selection, page) =>
        lifecycle.accept(selection, page),
    );
    let active = true;
    void lifecycle.restore(initialPage).then(
        (restored) => {
            if (active && restored !== undefined) onInitialRestored(restored);
        },
        (error: unknown) => {
            if (active) logger.error('Failed to restore initial tile list', error);
        },
    );
    return {
        cleanup: () => {
            active = false;
            lifecycle.stop();
            mounted.cleanup();
        },
    };
}

function mountBrowserWiring(
    ctx: TileListWiring,
    browserWiring: TileListBrowserWiring,
    onReload: (result: TileListPageLoadResult) => void | Promise<void>,
    pageLoads: OrderedPageLoads | null,
    onAccepted?: (selection: SelectionUpdate['selection'], page: TileListPageLayout) => void,
): { cleanup: () => void } {
    ctx.eventsIncoming = browserWiring.eventsIncoming;
    ctx.eventsOutgoing = browserWiring.eventsOutgoing;
    ctx.selectionEvents = browserWiring.selectionEvents;
    ctx.trackingService = browserWiring.trackingService;
    ctx.variationLoadingService = browserWiring.variationLoadingService;
    ctx.restoreScrollPosition = browserWiring.restoreScrollPosition;
    ctx.saveScrollSelector = browserWiring.saveScrollSelector;
    ctx.controller = new ProductTileController(
        browserWiring.variationLoadingService,
        browserWiring.trackingService,
        browserWiring.selectionEvents,
        browserWiring.blackFridaySliderLoader,
    );

    const reload = async (update: SelectionUpdate) => {
        logger.debug(`reload triggered for selection ${update.selection.rule}`);
        if (pageLoads) {
            const accepted = await pageLoads.reload(update);
            if (accepted !== undefined) {
                const { result } = accepted;
                if (result.renderedEvent) browserWiring.eventsOutgoing.publishTileListRendered(result.renderedEvent);
                await onReload(result);
                if (result.page && accepted.isCurrent()) onAccepted?.(accepted.update.selection, result.page);
            }
            return;
        }
        const result = await browserWiring.session.reload(update);
        if (result.renderedEvent) browserWiring.eventsOutgoing.publishTileListRendered(result.renderedEvent);
        await onReload(result);
    };
    const unsubscribe = browserWiring.selectionEvents.onUpdate(reload);

    logger.debug('Wiring mounted');
    return {
        cleanup: () => {
            pageLoads?.stop();
            unsubscribe();
            browserWiring.stopSelection();
        },
    };
}

const NULL_WIRING: TileListWiring = {
    /*                                                                             */
    /*                                                                              */
    get onex(): OnEx {
        throw new Error('injectWiring: no wiring context available (onex is unset)');
    },
    trackingService: null,
    variationLoadingService: null,
    controller: null,
    eventsIncoming: null,
    eventsOutgoing: null,
    selectionEvents: null,
    restoreScrollPosition: () => Promise.resolve(),
    saveScrollSelector: () => {},
};

/*                                                                              */
type RequiredWiring<K extends keyof TileListWiring> = Omit<TileListWiring, K> & {
    [P in K]-?: NonNullable<TileListWiring[P]>;
};

/**
 *
 *
 *
 *
 *
 *
 */
export function injectWiring(): TileListWiring;
export function injectWiring<K extends keyof TileListWiring>(required: readonly K[]): RequiredWiring<K>;
export function injectWiring(_required?: readonly (keyof TileListWiring)[]): TileListWiring {
    /*                                                                                                                             */
    return getContext<TileListWiring>(WIRING_KEY) ?? NULL_WIRING;
}
